import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { format, isThisMonth, parseISO } from 'date-fns';
import { useMemo } from 'react';

import { DocumentType, UsagePerForm } from '../../generated-api';
import ocrTypeNameDefinitions from '../../utils/ocrTypeNameDefinitions';

const calculateTotalUsage = (record: StatsFormTableData, documentTypes: Set<string>): number => {
  return Object.entries(record)
    .filter(([key, value]) => documentTypes.has(key) && typeof value === 'number')
    .reduce<number>((sum, [_, value]) => sum + (value as number), 0);
};

export interface StatsFormTableData extends Partial<Record<DocumentType, number>> {
  month: string;
  total: number;
  confirmed: boolean; // 未確定ならfalse
}

type Props = {
  usages: UsagePerForm[] | undefined;
};

const StatsFormTable = ({ usages }: Props) => {
  const dataSource = useMemo((): StatsFormTableData[] => {
    const formMonthToData = new Map<string, Partial<Record<DocumentType, number>>>();
    for (const usage of usages ?? []) {
      const monthStat = formMonthToData.get(usage.month) ?? {};
      monthStat[usage.ocr_type] = usage.usage;
      formMonthToData.set(usage.month, monthStat);
    }
    return [...formMonthToData.entries()].map(([month, usages]) => ({
      ...usages,
      month,
      confirmed: !isThisMonth(parseISO(month)),
      total: Object.values(usages).reduce<number>((previous, current) => previous + (current ?? 0), 0),
    }));
  }, [usages]);
  const columns = useMemo(
    (): ColumnsType<StatsFormTableData> => [
      {
        title: 'ご利用月',
        dataIndex: 'month',
        render: (value, record) => {
          const formattedDate = format(parseISO(value), 'yyyy/M');
          if (record.confirmed) {
            return formattedDate;
          } else {
            return '（未確定）' + formattedDate;
          }
        },
        width: 300,
        align: 'right',
      },
      {
        title: '利用枚数',
        children: [
          {
            title: ocrTypeNameDefinitions.furusato_tax,
            dataIndex: 'furusato_tax',
            render: renderUsageColumn,
            width: 240,
            align: 'right',
          },
          {
            title: ocrTypeNameDefinitions.medical_bill,
            dataIndex: 'medical_bill',
            render: renderUsageColumn,
            width: 240,
            align: 'right',
          },
          {
            title: ocrTypeNameDefinitions.bankbook,
            dataIndex: 'bankbook',
            render: renderUsageColumn,
            width: 240,
            align: 'right',
          },
          {
            title: ocrTypeNameDefinitions.card_statement,
            dataIndex: 'card_statement',
            render: renderUsageColumn,
            width: 240,
            align: 'right',
          },
          {
            title: ocrTypeNameDefinitions.receipt,
            dataIndex: 'receipt',
            render: (_: number, record: StatsFormTableData) => {
              const receiptTypes = new Set(['receipt', 'dual_horizontal_receipt', 'dual_vertical_receipt']);
              const usage = calculateTotalUsage(record, receiptTypes);
              return <>{(usage ?? 0).toLocaleString() + '枚'}</>;
            },
            width: 240,
            align: 'right',
          },
          {
            title: ocrTypeNameDefinitions.multi_receipt,
            dataIndex: 'multi_receipt',
            render: renderUsageColumn,
            width: 240,
            align: 'right',
          },
          {
            title: ocrTypeNameDefinitions.general_table,
            dataIndex: 'general_table',
            render: (_: number, record: StatsFormTableData) => {
              const generalTableTypes = new Set(['general_table', 'general_multi_table']);
              const usage = calculateTotalUsage(record, generalTableTypes);
              return <>{(usage ?? 0).toLocaleString() + '枚'}</>;
            },
            width: 240,
            align: 'right',
          },
          {
            title: ocrTypeNameDefinitions.invoice,
            dataIndex: 'invoice',
            render: renderUsageColumn,
            width: 240,
            align: 'right',
          },
          // ↓ RPA_SOL-636 で非表示に変更したが、将来的に再表示する可能性があるためコメントアウトで残す
          // {
          //   title: ocrTypeNameDefinitions.withholding_slip,
          //   dataIndex: 'withholding_slip',
          //   render: renderUsageColumn,
          //   width: 240,
          //   align: 'right',
          // },
          // {
          //   title: ocrTypeNameDefinitions.payment_record,
          //   dataIndex: 'payment_record',
          //   render: renderUsageColumn,
          //   width: 240,
          //   align: 'right',
          // },
          {
            title: '合計枚数',
            dataIndex: 'total',
            render: renderUsageColumn,
            width: 240,
            align: 'right',
          },
        ],
      },
    ],
    []
  );

  return <Table bordered={true} columns={columns} dataSource={dataSource} pagination={false} rowKey="month" />;
};

function renderUsageColumn(value: number) {
  return <>{(value ?? 0).toLocaleString() + '枚'}</>;
}

export default StatsFormTable;
